/** Central API layer - axios instance and fetch functions for TanStack Query. */
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

/** GET helpers that return response.data */
export async function fetchNotifications(pageSize = 10) {
  const { data } = await api.get(`${API}/notifications?page_size=${pageSize}`);
  return data;
}

export async function fetchSettings() {
  const { data } = await api.get(`${API}/settings`);
  return data;
}

export async function fetchConstituencies() {
  const { data } = await api.get(`${API}/constituencies`);
  return data;
}

export async function fetchCampaigns(constituencyId) {
  const { data } = await api.get(`${API}/campaigns?constituency_id=${constituencyId}`);
  return data;
}

export async function fetchRecords(params) {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') q.set(k, String(v));
  });
  const { data } = await api.get(`${API}/records?${q}`);
  return data;
}

export async function fetchRecordsBlocks(constituencyId, campaignId) {
  const { data } = await api.get(
    `${API}/records/blocks?constituency_id=${constituencyId}&campaign_id=${campaignId}`
  );
  return data;
}

export async function fetchDashboardStats(constituencyId, campaignId, majorityThreshold) {
  const { data } = await api.get(
    `${API}/dashboard/stats?constituency_id=${constituencyId}&campaign_id=${campaignId}&majority_threshold=${majorityThreshold}`
  );
  return data;
}

export async function fetchVotingBoxes(campaignId) {
  const { data } = await api.get(`${API}/campaigns/${campaignId}/voting-boxes`);
  return data;
}

export async function fetchMyVotingBoxes() {
  const { data } = await api.get(`${API}/my-voting-boxes`);
  return data;
}

export async function fetchVotingStats(campaignId) {
  const { data } = await api.get(`${API}/campaigns/${campaignId}/voting-stats`);
  return data;
}

export async function fetchVotingPublicLink(campaignId) {
  try {
    const { data } = await api.get(`${API}/campaigns/${campaignId}/public-link`);
    return data;
  } catch {
    return { enabled: false };
  }
}

export async function fetchTurnout(campaignId, token) {
  const url = token
    ? `${API}/public/campaigns/${campaignId}/turnout?token=${token}`
    : `${API}/public/campaigns/${campaignId}/turnout`;
  const { data } = await api.get(url);
  return data;
}

export async function fetchPublicCampaigns() {
  const { data } = await api.get(`${API}/public/campaigns`);
  return data;
}

export async function fetchCallerMe() {
  const { data } = await api.get(`${API}/callers/me`);
  return data;
}

export async function fetchCallers(status) {
  const url = status && status !== 'all'
    ? `${API}/callers?status=${status}`
    : `${API}/callers`;
  const { data } = await api.get(url);
  return data;
}

export async function fetchBlockAssignments(campaignId) {
  const url = campaignId
    ? `${API}/block-assignments?campaign_id=${campaignId}`
    : `${API}/block-assignments`;
  const { data } = await api.get(url);
  return data;
}

export async function fetchCallerAssignments() {
  const { data } = await api.get(`${API}/caller/assignments`);
  return data;
}

export async function fetchCallerRecords(campaignId, params = {}) {
  const { block, block_number, page, page_size, search } = params;
  const q = new URLSearchParams({ campaign_id: campaignId });
  if (block ?? block_number) q.set('block_number', block ?? block_number);
  if (page != null) q.set('page', String(page));
  if (page_size != null) q.set('page_size', String(page_size));
  if (search) q.set('search', search);
  const { data } = await api.get(`${API}/caller/records?${q}`);
  return data;
}

export async function fetchCallerStats(campaignId) {
  const { data } = await api.get(`${API}/caller/stats?campaign_id=${campaignId}`);
  return data;
}

export async function fetchMpConfig() {
  const { data } = await api.get(`${API}/mp/config`);
  return data;
}

export async function fetchMpBlocks() {
  const { data } = await api.get(`${API}/mp/blocks`);
  return data;
}

export async function fetchMpStats() {
  const { data } = await api.get(`${API}/mp/stats`);
  return data;
}

export async function fetchMpRequests(params) {
  const search = new URLSearchParams(params);
  const { data } = await api.get(`${API}/mp/requests?${search}`);
  return data;
}

export { api };
